import { LocalizationString } from '@celito.clients/assets';
import {
  AttributeTypeEnum,
  ButtonTypes,
  DateFormat,
} from '@celito.clients/enums';
import { ObjectAttributeDefinition } from '@celito.clients/types';
import { formatDate, isDateValid } from '@celito.clients/utils';
import { convert } from 'html-to-text';
import { Link } from 'react-router';

import { ColumnData, RowItem } from '../../../grid-view-new/src/types';
import { CustomButton } from '../../../shared';
import classes from '../reference-selector.module.css';

export const getPropertyFromAttributeDefinitions = <
  T extends keyof ObjectAttributeDefinition,
>(
  attributeDefinitions: Record<string, ObjectAttributeDefinition> | undefined,
  attributeName: string,
  propertyName: T,
  defaultValue: ObjectAttributeDefinition[T]
): ObjectAttributeDefinition[T] => {
  return attributeDefinitions?.[attributeName]?.[propertyName] ?? defaultValue;
};

export const getDefaultColumnsSelectorGridcolumns = (
  onItemDelete?: (item: RowItem) => void,
  isPreviewTab?: boolean,
  onColumnClick?: ColumnData['onColumnClick'],
  labelText = 'Label',
  referenceUrl = ''
) => {
  const courseGridColumns: ColumnData[] = [
    {
      name: labelText,
      isResizable: true,
      key: 'label',
      maxWidth: 200,
      minWidth: 100,
      fieldName: 'label',
      onColumnClick,
      showSortIconWhenUnsorted: true,
      onRender: (item: RowItem) => (
        <Link
          to={
            referenceUrl
              ? referenceUrl
                  .replace('{name}', item.name)
                  .replace('{version}', item.version)
              : ''
          }
          className={classes.hyperlink}
          target="_blank"
        >
          {item.label}
        </Link>
      ),
    },
    {
      name: 'Title',
      isResizable: true,
      key: 'title',
      maxWidth: 300,
      minWidth: 150,
      fieldName: 'title',
      showTooltip: true,
      onColumnClick,
      showSortIconWhenUnsorted: true,
    },
    {
      name: 'Status',
      isResizable: true,
      key: 'lifecycleStage',
      maxWidth: 150,
      minWidth: 50,
      fieldName: 'lifecycleStage',
      onColumnClick,
      showSortIconWhenUnsorted: true,
    },
    {
      name: 'Owner',
      isResizable: true,
      key: 'owner',
      maxWidth: 350,
      minWidth: 150,
      fieldName: 'owner.label',
      onColumnClick,
      showSortIconWhenUnsorted: true,
      data: {
        dataType: AttributeTypeEnum.Reference,
        columnNameToBePicked: 'label',
      },
    },
    {
      key: 'actions',
      minWidth: 75,
      maxWidth: 100,
      name: LocalizationString.ACTIONS,
      fieldName: 'actions',
      isResizable: true,
      onRender: (item?: RowItem, index?: number, column?: ColumnData) => (
        <CustomButton
          as="button"
          buttonType={ButtonTypes.Destructive}
          customStyles={classes.delete_btn}
          leftIcon="Delete"
          title="Delete"
          onClick={isPreviewTab ? undefined : () => onItemDelete?.(item!)}
          disabled={isPreviewTab}
        />
      ),
    },
  ];
  return courseGridColumns;
};

export const getDefaultColumnsSelectorGridcolumnsWithoutTitle = (
  onItemDelete?: (item: RowItem) => void,
  isPreviewTab?: boolean,
  onColumnClick?: ColumnData['onColumnClick'],
  labelText = 'Label',
  referenceUrl = ''
) => {
  const courseGridColumns: ColumnData[] = [
    {
      name: labelText,
      isResizable: true,
      key: 'label',
      maxWidth: 200,
      minWidth: 100,
      fieldName: 'label',
      onColumnClick,
      showSortIconWhenUnsorted: true,
      onRender: (item: RowItem) => (
        <Link
          to={
            referenceUrl
              ? referenceUrl
                  .replace('{name}', item.name)
                  .replace('{version}', item.version)
              : ''
          }
          className={classes.hyperlink}
          target="_blank"
        >
          {item.label}
        </Link>
      ),
    },
    {
      name: 'Status',
      isResizable: true,
      key: 'lifecycleStage',
      maxWidth: 150,
      minWidth: 50,
      fieldName: 'lifecycleStage',
      onColumnClick,
      showSortIconWhenUnsorted: true,
    },
    {
      name: 'Owner',
      isResizable: true,
      key: 'owner',
      maxWidth: 350,
      minWidth: 150,
      fieldName: 'owner.label',
      onColumnClick,
      showSortIconWhenUnsorted: true,
      data: {
        dataType: AttributeTypeEnum.Reference,
        columnNameToBePicked: 'label',
      },
    },
    {
      key: 'actions',
      minWidth: 75,
      maxWidth: 100,
      name: LocalizationString.ACTIONS,
      fieldName: 'actions',
      isResizable: true,
      onRender: (item?: RowItem, index?: number, column?: ColumnData) => (
        <CustomButton
          as="button"
          buttonType={ButtonTypes.Destructive}
          customStyles={classes.delete_btn}
          leftIcon="Delete"
          title="Delete"
          onClick={isPreviewTab ? undefined : () => onItemDelete?.(item!)}
          disabled={isPreviewTab}
        />
      ),
    },
  ];
  return courseGridColumns;
};

const HIDDEN_LOOKUP_COLUMNS = new Set([
  'id',
  'name',
  'ownerType',
  'objectDefinitionName',
  'createdAtUtc',
  'modifiedAtUtc',
  'createdByUserId',
  'modifiedByUserId',
  'deletedAtUtc',
  'GlobalUUID',
]);

export const buildDynamicColumns = (
  lookupColumnNames: string[],
  attributeDefinitions: Record<string, ObjectAttributeDefinition> | undefined,
  onItemDelete?: (item: RowItem) => void,
  isPreviewTab?: boolean,
  onColumnClick?: ColumnData['onColumnClick'],
  referenceUrl = ''
): ColumnData[] => {
  const displayColumns = lookupColumnNames.filter(
    (col) => !HIDDEN_LOOKUP_COLUMNS.has(col)
  );

  const columns: ColumnData[] = displayColumns
    .map((colName, index) => {
      const attrDef = Object.values(attributeDefinitions ?? {}).find(
        (attr) => attr.columnName === colName || attr.name === colName
      );

      if (attrDef?.isInternal || attrDef?.isHidden) return null;

      const isFirstColumn = index === 0;
      const isReferenceType = attrDef?.dataType === AttributeTypeEnum.Reference;
      const isPicklistType =
        attrDef?.dataType === AttributeTypeEnum.Picklist ||
        attrDef?.dataType === AttributeTypeEnum.FixedList ||
        attrDef?.dataType === AttributeTypeEnum.Dropdown;
      const isDateType = attrDef?.dataType === AttributeTypeEnum.Date;
      const isDateTimeType = attrDef?.dataType === AttributeTypeEnum.DateTime;
      const isRichTextType = attrDef?.dataType === AttributeTypeEnum.RichText;

      const column: ColumnData = {
        name: attrDef?.label ?? colName,
        isResizable: true,
        key: colName,
        maxWidth: 300,
        minWidth: 100,
        fieldName: isReferenceType ? `${colName}.label` : colName,
        onColumnClick,
        showSortIconWhenUnsorted: true,
        ...(isReferenceType && {
          data: {
            dataType: AttributeTypeEnum.Reference,
            columnNameToBePicked: 'label',
          },
        }),
        ...(isPicklistType && {
          onRender: (item: RowItem) => {
            const val = item[colName];
            if (val == null) return '';
            if (typeof val === 'object') {
              return (val.label ?? val.displayLabel ?? '') as string;
            }
            return String(val);
          },
        }),
        ...((isDateType || isDateTimeType) && {
          onRender: (item: RowItem) => {
            const val = item[colName];
            if (val == null || !isDateValid(val as string)) return '';
            return formatDate(
              val as string,
              isDateTimeType ? DateFormat.DateTime : DateFormat.Date
            );
          },
        }),
      };

      if (isFirstColumn && referenceUrl) {
        column.onRender = (item: RowItem) => (
          <Link
            to={referenceUrl
              .replace('{name}', item.name)
              .replace('{version}', item.version)}
            className={classes.hyperlink}
            target="_blank"
          >
            {item[colName]}
          </Link>
        );
      }

      if (!column.onRender && isRichTextType) {
        column.onRender = (item: RowItem) => {
          const val = item[colName];
          if (val == null) return '';
          return convert(String(val));
        };
      }

      if (!column.onRender && !isReferenceType) {
        column.onRender = (item: RowItem) => {
          const val = item[colName];
          if (val == null) return '';
          if (typeof val === 'object') {
            return (val.label ?? val.displayLabel ?? val.name ?? '') as string;
          }
          return String(val);
        };
      }

      return column;
    })
    .filter(Boolean) as ColumnData[];

  columns.push({
    key: 'actions',
    minWidth: 75,
    maxWidth: 100,
    name: LocalizationString.ACTIONS,
    fieldName: 'actions',
    isResizable: true,
    onRender: (item?: RowItem) => (
      <CustomButton
        as="button"
        buttonType={ButtonTypes.Destructive}
        customStyles={classes.delete_btn}
        leftIcon="Delete"
        title="Delete"
        onClick={isPreviewTab ? undefined : () => onItemDelete?.(item!)}
        disabled={isPreviewTab}
      />
    ),
  });

  return columns;
};
